import { useEffect, useState } from "react";

import { supabase } from "@/components/atomic-crm/providers/supabase/supabase";
import type { FiltroSeguradora, TabelaSeguros } from "./seguradoras";

/**
 * O PostgREST do projeto devolve no máximo 1000 linhas por consulta, SEM erro
 * (`max_rows` em db/config.toml): `.limit(5000)` volta 1000. Por isso a leitura
 * é paginada de 1000 em 1000 até a página vir incompleta.
 */
const LINHAS_POR_PAGINA = 1000;

/**
 * Chave primária de cada tabela (migrations 157, 169, 170, 188): desempate da
 * ordenação. Paginar só por uma coluna com repetição (competência, dia) deixa
 * o banco devolver os empates em ordem diferente a cada página — linha
 * repetida numa e sumida na outra.
 */
const CHAVE: Record<TabelaSeguros, string[]> = {
  seguros_propostas_v: ["seguradora", "numero"],
  seguros_apolices_v: ["seguradora", "numero"],
  seguros_apolice_coberturas_v: ["seguradora", "numero_apolice", "identificador"],
  seguros_cobrancas_v: ["seguradora", "numero_apolice", "identificador_cobranca"],
  seguros_comissoes_v: ["seguradora", "numero_apolice", "numero_lancamento", "produtor_documento_digits", "tipo_lancamento"],
  seguros_corretores_v: ["seguradora", "documento_digits"],
  seguros_coberturas_catalogo_v: ["seguradora", "codigo"],
  seguros_faturas: ["seguradora", "id"],
  seguros_pendencias: ["seguradora", "id_pendencia"],
  seguros_sinistros: ["seguradora", "identificador_sinistro", "beneficiario_cpf_digits"],
  seguros_movimento_diario: ["dia", "seguradora", "corretor_documento_digits"],
  seguros_inadimplencia_corretora: ["seguradora", "corretor_documento_digits"],
  seguros_inadimplencia_diaria: ["dia", "seguradora", "corretor_documento_digits"],
  seguros_cobranca_apolices: ["dia", "seguradora", "numero_apolice"],
  seguros_cobranca_movimento: ["seguradora", "numero_apolice"],
  seguros_cobranca_fotografia: ["dia_atual"],
};

/**
 * Lê uma view/tabela `seguros_*` com o filtro de seguradora aplicado no banco.
 *
 * `limite` é o teto do que a tela aceita carregar, não o tamanho da consulta:
 * a leitura pagina até ele. `igual` filtra no banco (ex.: competência), para
 * a tela não trazer o histórico inteiro e cortar depois. `pular` segura a
 * consulta enquanto a tela ainda não sabe o que pedir.
 *
 * O `as any` é contido aqui de propósito: os tipos gerados do Supabase ainda
 * não conhecem `seguros_*`. Quando `database.generated.ts` for regenerado, é
 * só tirar o cast e o resto da UI não muda.
 */
export function useTabelaSeguros<T>(
  tabela: TabelaSeguros,
  opcoes: {
    select?: string;
    ordem?: { coluna: string; asc?: boolean };
    limite?: number;
    seguradora?: FiltroSeguradora;
    igual?: Record<string, string>;
    pular?: boolean;
  } = {},
) {
  const [linhas, setLinhas] = useState<T[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  const { select = "*", ordem, limite = 20_000, seguradora = "todas", igual, pular = false } = opcoes;
  const ordemColuna = ordem?.coluna;
  const ordemAsc = ordem?.asc ?? false;
  // Objeto novo a cada render não pode disparar nova consulta: compara o conteúdo.
  const igualChave = JSON.stringify(igual ?? {});

  useEffect(() => {
    if (pular) return;
    let ativo = true;
    (async () => {
      setCarregando(true);
      setErro(null);
      const filtros = JSON.parse(igualChave) as Record<string, string>;
      const montar = () => {
        let q = supabase.from(tabela as any).select(select);
        if (seguradora !== "todas") q = q.eq("seguradora", seguradora);
        for (const [coluna, valor] of Object.entries(filtros)) q = q.eq(coluna, valor);
        if (ordemColuna) q = q.order(ordemColuna, { ascending: ordemAsc, nullsFirst: false });
        for (const coluna of CHAVE[tabela]) {
          if (coluna !== ordemColuna) q = q.order(coluna, { ascending: true });
        }
        return q;
      };
      const todas: T[] = [];
      for (let de = 0; de < limite; de += LINHAS_POR_PAGINA) {
        const ate = Math.min(de + LINHAS_POR_PAGINA, limite) - 1;
        const { data, error } = await montar().range(de, ate);
        if (!ativo) return;
        if (error) {
          setErro(error.message);
          setCarregando(false);
          return;
        }
        const pagina = (data ?? []) as T[];
        todas.push(...pagina);
        if (pagina.length < ate - de + 1) break;
      }
      setLinhas(todas);
      setCarregando(false);
    })();
    return () => {
      ativo = false;
    };
  }, [tabela, select, ordemColuna, ordemAsc, limite, seguradora, igualChave, pular]);

  return { linhas, carregando, erro };
}

/**
 * Consulta pontual por documento do segurado, fora do hook de lista: a ficha
 * do contato busca por CPF e não tem filtro de seguradora.
 */
export async function buscarPorSegurado<T>(
  tabela: Extract<TabelaSeguros, "seguros_apolices_v" | "seguros_propostas_v">,
  select: string,
  cpfDigits: string,
  ordem?: { coluna: string; asc?: boolean },
): Promise<T[]> {
  let q = supabase.from(tabela as any).select(select).eq("segurado_cpf_digits", cpfDigits);
  if (ordem) q = q.order(ordem.coluna, { ascending: ordem.asc ?? false, nullsFirst: false });
  const { data } = await q;
  return (data ?? []) as T[];
}

/** Seguradoras cadastradas e ativas; vazio quando a tabela não responde. */
export async function listarSeguradorasAtivas(): Promise<{ codigo: string; nome: string }[]> {
  const { data } = await supabase
    .from("seguros_seguradoras" as any)
    .select("codigo, nome")
    .eq("ativa", true)
    .order("codigo", { ascending: true });
  return (data ?? []) as unknown as { codigo: string; nome: string }[];
}

/** Todos os meses "AAAA-MM" de `de` até `ate`, do mais recente para o mais antigo. */
export function mesesEntre(de: string, ate: string): string[] {
  const meses: string[] = [];
  let [ano, mes] = ate.split("-").map(Number);
  const [anoFim, mesFim] = de.split("-").map(Number);
  while (ano > anoFim || (ano === anoFim && mes >= mesFim)) {
    meses.push(`${ano}-${String(mes).padStart(2, "0")}`);
    mes -= 1;
    if (mes === 0) {
      mes = 12;
      ano -= 1;
    }
  }
  return meses;
}

/**
 * Meses de uma coluna "AAAA-MM" (ex.: competência), do mais recente para o
 * mais antigo. Duas consultas de uma linha (primeiro e último mês) em vez de
 * ler a tabela inteira só para montar o seletor.
 */
export function useMesesDisponiveis(tabela: TabelaSeguros, coluna: string, seguradora: FiltroSeguradora) {
  const [meses, setMeses] = useState<string[]>([]);
  const [carregando, setCarregando] = useState(true);

  useEffect(() => {
    let ativo = true;
    (async () => {
      setCarregando(true);
      const ponta = (asc: boolean) => {
        let q = supabase.from(tabela as any).select(coluna).not(coluna, "is", null);
        if (seguradora !== "todas") q = q.eq("seguradora", seguradora);
        return q.order(coluna, { ascending: asc }).limit(1);
      };
      const [primeiro, ultimo] = await Promise.all([ponta(true), ponta(false)]);
      if (!ativo) return;
      const de = (primeiro.data?.[0] as Record<string, string> | undefined)?.[coluna]?.slice(0, 7);
      const ate = (ultimo.data?.[0] as Record<string, string> | undefined)?.[coluna]?.slice(0, 7);
      setMeses(de && ate ? mesesEntre(de, ate) : []);
      setCarregando(false);
    })();
    return () => {
      ativo = false;
    };
  }, [tabela, coluna, seguradora]);

  return { meses, carregando };
}
